'use client';

import { c as _c } from "react/compiler-runtime";
import { DebateSkeleton } from '@/components/social/debate/debate-skeleton';
import { ThesisCard } from '@/components/social/debate/thesis-card';
import type { ThesisReplyWriteAccess } from '@/components/social/debate/thesis-reply-write-access';
import type { ThesisItem } from '@/components/social/types';
import { Separator } from '@/components/ui/app-separator';
import { EmptyState } from '@/components/ui/empty-state';
import { Comment03IconSolid } from '@/components/ui/icon/glyphs';
import { InfiniteList, type InfiniteListScrollMode } from '@/components/ui/infinite-list';
function dedupBySignature(items: ThesisItem[]): ThesisItem[] {
  const seen = new Set<string>();
  return items.filter(item => {
    if (seen.has(item.signature)) return false;
    seen.add(item.signature);
    return true;
  });
}

/**
 * The pagination + load-state contract, exactly as it arrives from the
 * `useGetThesesInfinite()` result at every call site. Grouped into one prop so
 * the feed receives the fetch state as the single cohesive unit it already is,
 * rather than four loose flags.
 */
interface ThesisFeedPagination {
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** Used only to gate the initial skeleton (`isLoading && pageCount === 0`). */
  pageCount: number;
}
interface ThesisFeedProps {
  theses: ThesisItem[];
  pagination: ThesisFeedPagination;
  isAuthenticated: boolean;
  userId: string | null;
  /** Shared market-level write access. Reading threads never depends on it. */
  replyWriteAccess?: ThesisReplyWriteAccess;
  /** Opens the owning market's mobile trade sheet. */
  onOpenTradeForm?: () => void;
  /** Empty-state headline — the primary "no theses" message. Pre-translated. */
  emptyTitle: string;
  /** Empty-state supporting copy below the headline. Pre-translated. */
  emptyDescription: string;
  /**
   * `'debate'` (default) is used inside the market-detail Debate tab; the
   * portfolio Theses panel passes `'portfolio'` so each row renders as a
   * pressable card with the parent market title.
   */
  variant?: 'debate' | 'portfolio';
  showMarketLink?: boolean;
  /** Cap on the scroll viewport. Default `560`. */
  maxHeight?: number | string;
  /** Let the page own scrolling below the desktop breakpoint. */
  scrollMode?: InfiniteListScrollMode;
}
const THESIS_FEED_DIVIDER_CLASS = 'h-[length:var(--border-width-hairline)] min-h-[0.5px] shrink-0 bg-border-secondary';
export function ThesisFeed(t0) {
  const $ = _c(27);
  const {
    theses,
    pagination,
    isAuthenticated,
    userId,
    replyWriteAccess,
    onOpenTradeForm,
    emptyTitle,
    emptyDescription,
    variant: t1,
    showMarketLink: t2,
    maxHeight: t3,
    scrollMode: t4
  } = t0;
  const variant = t1 === undefined ? "debate" : t1;
  const showMarketLink = t2 === undefined ? false : t2;
  const maxHeight = t3 === undefined ? 560 : t3;
  const scrollMode = t4 === undefined ? "contained" : t4;
  const {
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    pageCount
  } = pagination;
  let t5;
  if ($[0] !== theses) {
    t5 = dedupBySignature(theses);
    $[0] = theses;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const dedupedTheses = t5;
  let t6;
  if ($[2] !== isAuthenticated || $[3] !== onOpenTradeForm || $[4] !== replyWriteAccess || $[5] !== showMarketLink || $[6] !== userId || $[7] !== variant) {
    t6 = thesis_0 => <ThesisCard thesis={thesis_0} isAuthenticated={isAuthenticated} userId={userId} {...replyWriteAccess !== undefined ? {
      replyWriteAccess
    } : {}} {...onOpenTradeForm !== undefined ? {
      onOpenTradeForm
    } : {}} variant={variant} showMarketLink={showMarketLink} />;
    $[2] = isAuthenticated;
    $[3] = onOpenTradeForm;
    $[4] = replyWriteAccess;
    $[5] = showMarketLink;
    $[6] = userId;
    $[7] = variant;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== variant) {
    t7 = variant === "debate" ? <Separator className={THESIS_FEED_DIVIDER_CLASS} /> : null;
    $[9] = variant;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = isLoading && pageCount === 0;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DebateSkeleton />;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== emptyDescription || $[13] !== emptyTitle) {
    t10 = <EmptyState icon={Comment03IconSolid} title={emptyTitle} description={emptyDescription} />;
    $[12] = emptyDescription;
    $[13] = emptyTitle;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = variant === "portfolio" ? "flex flex-col gap-y-4" : "flex flex-col gap-y-2";
  let t12;
  if ($[15] !== dedupedTheses || $[16] !== fetchNextPage || $[17] !== hasNextPage || $[18] !== isFetchingNextPage || $[19] !== maxHeight || $[20] !== scrollMode || $[21] !== t10 || $[22] !== t11 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t12 = <InfiniteList items={dedupedTheses} getItemKey={_temp} renderItem={t6} separator={t7} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} isInitialLoading={t8} loadingPlaceholder={t9} emptyState={t10} listClassName={t11} maxHeight={maxHeight} scrollMode={scrollMode} />;
    $[15] = dedupedTheses;
    $[16] = fetchNextPage;
    $[17] = hasNextPage;
    $[18] = isFetchingNextPage;
    $[19] = maxHeight;
    $[20] = scrollMode;
    $[21] = t10;
    $[22] = t11;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
function _temp(thesis) {
  return thesis.signature;
}